import { redirect } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import {
  LOGIN_PATH,
  NO_ACCESS_PATH,
  canAccessGroup,
  hasSurface,
  surfaceToHome,
  tryGetProfile,
} from "@core/auth";
import type { RouteGroup, Surface } from "@core/auth";
import { hasCapability } from "@core/permissions";
import type { Capability } from "@core/permissions";

// `-` prefix: TanStack's route generator ignores this file (shared route glue, not a route).

/**
 * Authentication only — no surface or group assertion.
 *
 * For routes any signed-in user may see whatever their bundles are. Today that is the
 * /no-access terminal, which exists precisely FOR the surfaces belonging to no tree: gating it
 * on a group would bounce those users to their landing, which IS this page, behind the guard
 * that just bounced them.
 */
export async function requireAuth(
  queryClient: QueryClient,
  pathname: string
): Promise<void> {
  const profile = await tryGetProfile(queryClient);
  if (!profile) {
    throw redirect({ to: LOGIN_PATH, search: { next: pathname } });
  }
}

/**
 * Group guard for the /staff/* and /applicant/* route trees. Two gates, in order:
 *   1. Authentication — unauthenticated visitors go to login, preserving the attempted path as
 *      `next` so the post-login redirect can honor the deep link.
 *   2. Group membership — a caller with no surface in `group` is sent to their own home.
 * Both group decisions resolve through the single `@core/auth` helper; no inline bundle checks.
 */
export async function requireGroup(
  queryClient: QueryClient,
  group: RouteGroup,
  pathname: string
): Promise<void> {
  const profile = await tryGetProfile(queryClient);
  if (!profile) {
    throw redirect({ to: LOGIN_PATH, search: { next: pathname } });
  }
  if (!canAccessGroup(profile.available_surfaces, group)) {
    throw redirect({ to: surfaceToHome(profile.default_surface) });
  }
}

/**
 * Per-screen guard for a route that belongs to ONE surface. Runs the same authentication gate
 * as {@link requireGroup}, then asserts the specific surface — the group guard above only
 * proves the caller is staff, which is not enough while phase 1 ships the administrator
 * surface alone. A staff member without the surface is sent to their own landing (the
 * no-access terminal for the not-yet-built surfaces), never left on a screen they can't use.
 */
export async function requireSurface(
  queryClient: QueryClient,
  surface: Surface,
  pathname: string
): Promise<void> {
  const profile = await tryGetProfile(queryClient);
  if (!profile) {
    throw redirect({ to: LOGIN_PATH, search: { next: pathname } });
  }
  if (!hasSurface(profile.available_surfaces, surface)) {
    throw redirect({ to: surfaceToHome(profile.default_surface) });
  }
}

/**
 * Per-screen guard for a route TWO surfaces may both reach — the intake operations screens
 * (queue/applications/waiting/dossier detail), which `program_coordinator` and `administrator`
 * are already permission-equal on server-side (confirmed against `_require_round`/`CAPABILITIES`
 * in `operations/services.py` — both hold `acquire_operations.view_operations` and the rest).
 * `requireSurface` alone can't express this: it asserts exactly one surface, and duplicating the
 * route per surface would fork the screen instead of sharing it. Same two gates as
 * {@link requireSurface}, just an `.some()` over the list instead of one `hasSurface` check.
 */
export async function requireAnySurface(
  queryClient: QueryClient,
  surfaces: readonly Surface[],
  pathname: string
): Promise<void> {
  const profile = await tryGetProfile(queryClient);
  if (!profile) {
    throw redirect({ to: LOGIN_PATH, search: { next: pathname } });
  }
  if (
    !surfaces.some((surface) => hasSurface(profile.available_surfaces, surface))
  ) {
    throw redirect({ to: surfaceToHome(profile.default_surface) });
  }
}

/**
 * Guard for a route that needs one capability (the staff frame needs `team.read`). Same two gates
 * as the guards above: signed in (else login, keeping the path as `next`), then the capability.
 * An account without it is sent to its own landing; a landing that would be the staff frame
 * itself goes to the no-access terminal instead, so the redirect can never loop. This is UX: the
 * API checks every request again.
 */
export async function requireCapability(
  queryClient: QueryClient,
  capability: Capability,
  pathname: string
): Promise<void> {
  const profile = await tryGetProfile(queryClient);
  if (!profile) {
    throw redirect({ to: LOGIN_PATH, search: { next: pathname } });
  }
  if (!hasCapability(profile.capabilities, capability)) {
    const home = surfaceToHome(profile.default_surface);
    throw redirect({ to: home === "/team" ? NO_ACCESS_PATH : home });
  }
}
